import Cookie from "js-cookie";
import {
  AUTH_COOKIE_KEYS,
  AUTH_LS_KEYS,
  getAccessTokenCookieOptions,
} from "../constants/authStorage";

const cookieOpts = () => getAccessTokenCookieOptions();

export const storage = {
  localStorage: {
    set: (key, value) =>
      localStorage?.setItem(key, JSON.stringify(value)),
    get: (key) => {
      try {
        return JSON.parse(localStorage?.getItem(key) ?? "null");
      } catch {
        return null;
      }
    },
    remove: (key) => localStorage?.removeItem(key),
  },
  cookieStorage: {
    set: (key, value, options) =>
      Cookie?.set(key, value, { ...cookieOpts(), ...options }),
    get: (key) => Cookie?.get(key) || "",
    remove: (key, options) =>
      Cookie.remove(key, { path: "/", ...cookieOpts(), ...options }),
  },
};

/**
 * Access token: localStorage first (works reliably after refresh on prod),
 * then cookie. If found only in cookie, mirror to localStorage once.
 */
function readLegacyLocalStorageAccessToken() {
  if (typeof localStorage === "undefined") return null;
  const raw = (localStorage.getItem("access_token") || "").trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed.trim() : null;
  } catch {
    return raw;
  }
}

export function readPersistedAccessToken() {
  if (typeof localStorage === "undefined") {
    return (Cookie?.get(AUTH_COOKIE_KEYS.ACCESS_TOKEN) || "").trim() || null;
  }
  const fromLs = (localStorage.getItem(AUTH_LS_KEYS.ACCESS_TOKEN) || "").trim();
  if (fromLs) return fromLs;

  const legacy = readLegacyLocalStorageAccessToken();
  if (legacy) {
    try {
      localStorage.setItem(AUTH_LS_KEYS.ACCESS_TOKEN, legacy);
      localStorage.removeItem("access_token");
    } catch {
      /* ignore */
    }
    return legacy;
  }

  const fromCookie = (
    Cookie?.get(AUTH_COOKIE_KEYS.ACCESS_TOKEN) || ""
  ).trim();
  if (fromCookie) {
    try {
      localStorage.setItem(AUTH_LS_KEYS.ACCESS_TOKEN, fromCookie);
    } catch {
      /* quota / private mode */
    }
    return fromCookie;
  }
  return null;
}

/** Persist JWT: plain string in localStorage + mirrored cookie. */
export function persistAccessToken(token) {
  if (token == null || String(token).trim() === "") {
    clearPersistedAccessToken();
    return;
  }
  const value = String(token).trim();
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(AUTH_LS_KEYS.ACCESS_TOKEN, value);
    } catch {
      /* still set cookie */
    }
  }
  Cookie?.set(AUTH_COOKIE_KEYS.ACCESS_TOKEN, value, cookieOpts());
}

export function clearPersistedAccessToken() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_LS_KEYS.ACCESS_TOKEN);
  }
  Cookie?.remove(AUTH_COOKIE_KEYS.ACCESS_TOKEN, {
    path: "/",
  });
}

/** Clear token + all JSON auth keys (same as logout / 401). */
export function clearAllAuthPersistence() {
  clearPersistedAccessToken();
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("access_token");
  localStorage.removeItem(AUTH_LS_KEYS.COOP_PROFILE);
  localStorage.removeItem(AUTH_LS_KEYS.COMPANY_ID);
  localStorage.removeItem(AUTH_LS_KEYS.USER_PROFILE);
  localStorage.removeItem(AUTH_LS_KEYS.USER_TYPE);
  localStorage.removeItem(AUTH_LS_KEYS.ENUMS);
}
